import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AgendaAgendaOpcao,
  AgendaClassificacao,
  AgendaContexto,
  AgendaEstabelecimento,
  AgendaGradeLinha,
  AgendaMedico,
  AgendaMesDia,
  AgendaOpcao,
} from '../../../interfaces/agenda';
import { AgendaModaisService } from '../../../services/agenda/agenda-modais.service';
import { AgendaService, mensagemErroAgenda } from '../../../services/agenda/agenda.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { AgendaAgendamentoComponent } from './agenda-agendamento/agenda-agendamento.component';
import { AgendaModoAgendamento, podeEncaixar } from './agenda-agendamento/agenda-agendamento';
import { AgendaAcaoGradeEvento, AgendaGradeComponent, AgendaMenuLinhaEvento } from './agenda-grade/agenda-grade.component';
import { AgendaReservaComponent } from './agenda-reserva/agenda-reserva.component';
import { AgendaTransferirComponent } from './agenda-transferir/agenda-transferir.component';
import { AgendaAlterarComponent } from './agenda-alterar/agenda-alterar.component';
import { AgendaCancelarComponent } from './agenda-cancelar/agenda-cancelar.component';
import { AgendaTrocarPacienteComponent } from './agenda-trocar-paciente/agenda-trocar-paciente.component';
import { AgendaDesvincularComponent } from './agenda-desvincular/agenda-desvincular.component';
import { AgendaDisponibilidadeComponent } from './agenda-disponibilidade/agenda-disponibilidade.component';
import { AgendasDoDiaComponent } from './agendas-do-dia/agendas-do-dia.component';
import { AgendaExameInicial, AgendaHorarioEscolhido } from './agenda-disponibilidade/agenda-disponibilidade';
import { AgendaPacienteEscolhido } from './agenda-paciente/agenda-paciente';
import { motivoAcaoSelecao } from './agenda-acoes';
import { ehMarcado, opcoesClassificacao } from './agenda-grade/agenda-grade';
import {
  AgendaFiltroAplicado,
  AgendaFiltroEntrada,
  AgendaFiltroEstado,
  AgendaTipoFiltro,
  AgendaVisao,
  areaAtuacaoDoTipo,
  autoCargaPronta,
  codigoTipoAgenda,
  dataLocal,
  dicaFiltro,
  filtroInicial,
  filtroPronto,
  mesmaSelecao,
  somarDias,
  tenantTemOdonto,
  tipoComEspecialidade,
} from './agenda-filtro';
import { AgendaMenuItem, AgendaMenuLinha, AgendaMenuRegra, itensMenuLinha, itensMenuSelecao, posicaoMenu, regraFilaMedica } from './agenda-menu';
import { celulasMes, mesAnterior as mesAnteriorDe, mesSeguinte as mesSeguinteDe, rotuloMes } from './agenda-mes';

export interface AgendaChip {
  chave: 'tipo' | 'estabelecimento' | 'especialidade' | 'medico' | 'agenda' | 'classif' | 'cancelados' | 'soAgendados' | 'soLivres';
  rotulo: string;
  removivel: boolean;
}

/** Paciente da linha para a Conta ou o Histórico, que são do dashboard. */
export interface AgendaPacienteFila {
  cdPessoaFisica: number;
  nome: string;
  cpf: string;
  nrAtendimento: number | null;
}

/** Agenda para o Ver Fila do dashboard; o nome cobre agenda que não vier na lista dele. */
export interface AgendaFilaMedica {
  cdAgenda: number;
  nmAgenda: string | null;
}

type Lista = 'estabelecimentos' | 'especialidades' | 'medicos' | 'agendas';

const ESPERA_AUTO_CARGA_MS = 250;
const DURACAO_SUCESSO_MS = 6000;
export const CHAVE_FILTROS_RECOLHIDOS = 'agenda.filtrosRecolhidos';
const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

interface AgendaTipoOpcao { valor: AgendaTipoFiltro; rotulo: string }
const TIPOS: AgendaTipoOpcao[] = [
  { valor: 'CONSULTA', rotulo: 'Consulta' },
  { valor: 'EXAME', rotulo: 'Exame' },
  { valor: 'INTEGRADO', rotulo: 'Integrado' },
];
const TIPOS_SIM: AgendaTipoOpcao[] = [...TIPOS, { valor: 'ODONTOLOGIA', rotulo: 'Odontologia' }];

@Component({
  selector: 'app-agenda',
  standalone: true,
  imports: [
    CommonModule, FormsModule, AgendaGradeComponent, AgendaAgendamentoComponent, AgendaReservaComponent,
    AgendaTransferirComponent, AgendaAlterarComponent, AgendaCancelarComponent, AgendaTrocarPacienteComponent, AgendaDesvincularComponent,
    AgendaDisponibilidadeComponent, AgendasDoDiaComponent,
  ],
  templateUrl: './agenda.component.html',
  styleUrl: './agenda.component.scss',
})
export class AgendaComponent implements OnInit, OnDestroy {
  /** Lido só ao abrir; null = unidade atual, hoje. */
  @Input() inicial: AgendaFiltroEntrada | null = null;
  /** Lida só ao abrir; depois vale a última visão escolhida enquanto o painel estiver aberto. */
  @Input() visaoInicial: AgendaVisao = 'dia';
  @Output() fechar = new EventEmitter<void>();
  /** "Conta do paciente": sempre com nrAtendimento. */
  @Output() abrirConta = new EventEmitter<AgendaPacienteFila>();
  @Output() abrirHistorico = new EventEmitter<AgendaPacienteFila>();
  @Output() abrirFilaMedica = new EventEmitter<AgendaFilaMedica>();

  get tipos(): AgendaTipoOpcao[] {
    return this.temOdonto ? TIPOS_SIM : TIPOS;
  }

  get temOdonto(): boolean { return tenantTemOdonto(this.contexto()?.tenant); }

  readonly contexto = signal<AgendaContexto | null>(null);
  /** O que a grade consome; null = filtro incompleto. */
  readonly filtroAplicado = signal<AgendaFiltroAplicado | null>(null);

  f: AgendaFiltroEstado = filtroInicial(dataLocal(new Date()));

  estabelecimentos: AgendaEstabelecimento[] = [];
  especialidades: AgendaOpcao[] = [];
  medicos: AgendaMedico[] = [];
  agendas: AgendaAgendaOpcao[] = [];
  classificacoes: AgendaClassificacao[] = [];

  carregando: Record<Lista, boolean> = { estabelecimentos: false, especialidades: false, medicos: false, agendas: false };
  erro = '';

  /** Mês: mesma seleção da grade, data trocada por mês. Agendas do Dia: só unidade e data. */
  visao: AgendaVisao = 'dia';
  mesAtual = this.f.data.slice(0, 7);
  diasMes: AgendaMesDia[] = [];
  carregandoMes = false;
  erroMes = '';

  /** filtro e exame só vêm do Encontrar disponibilidade: o horário pode ser de outra agenda e unidade. */
  modalAgendamento: {
    modo: AgendaModoAgendamento; linha: AgendaGradeLinha | null; filtro?: AgendaFiltroAplicado; exame?: AgendaExameInicial | null;
    paciente?: AgendaPacienteEscolhido | null;
  } | null = null;
  modalDisponibilidade = false;
  modalReserva: AgendaGradeLinha | null = null;
  /** Linhas pedidas para transferir; o modal separa as elegíveis. */
  modalTransferir: AgendaGradeLinha[] | null = null;
  modalAlterar: AgendaGradeLinha[] | null = null;
  /** Linhas pedidas para cancelar; o modal separa as que só Normal/Confirmada permitem. */
  modalCancelar: AgendaGradeLinha[] | null = null;
  modalTrocarPaciente: AgendaGradeLinha | null = null;
  modalDesvincular: AgendaGradeLinha | null = null;
  sucesso = '';
  private timerSucesso: ReturnType<typeof setTimeout> | null = null;

  @ViewChild(AgendaGradeComponent) private grade?: AgendaGradeComponent;
  @ViewChild(AgendaDisponibilidadeComponent) private disponibilidade?: AgendaDisponibilidadeComponent;

  /**
   * Menu ⋮/botão direito de uma linha da grade. `alvo` são as linhas que a ação em lote atinge:
   * a própria linha, ou a seleção inteira quando o clique direito a manteve.
   */
  menu: { linha: AgendaGradeLinha; alvo: AgendaGradeLinha[]; podeEncaixe: boolean; motivoEncaixe: string; top: number; left: number } | null = null;
  private fecharMenuModal: (() => void) | null = null;

  private unidade: number | null = null;
  /** A do login, que é a do Ver Fila; `unidade` pode vir do contexto. */
  private unidadeLogada: number | null = null;
  private timerAuto: ReturnType<typeof setTimeout> | null = null;
  // Respostas de uma cascata anterior chegando por último não podem sobrescrever a atual.
  private seq: Record<Lista, number> = { estabelecimentos: 0, especialidades: 0, medicos: 0, agendas: 0 };
  private seqMes = 0;

  constructor(
    private agendaService: AgendaService,
    private localStorage: LocalStorageService,
    private modais: AgendaModaisService,
  ) {}

  async ngOnInit() {
    this.filtrosRecolhidos = this.lerRecolhidos();
    this.visao = this.visaoInicial;
    await this.iniciar(this.inicial);
  }

  /** Lateral de filtros recolhida: a grade ganha a largura toda. Lembrado por navegador. */
  filtrosRecolhidos = false;

  alternarFiltros() {
    this.filtrosRecolhidos = !this.filtrosRecolhidos;
    // Storage pode faltar (janela privada, bloqueio de site): segue sem lembrar.
    try {
      this.localStorage.set(CHAVE_FILTROS_RECOLHIDOS, this.filtrosRecolhidos);
    } catch { /* sem storage */ }
  }

  private lerRecolhidos(): boolean {
    try {
      return this.localStorage.get(CHAVE_FILTROS_RECOLHIDOS) === true;
    } catch {
      return false;
    }
  }

  ngOnDestroy() {
    this.cancelarAuto();
    this.fecharMenu();
    if (this.timerSucesso) clearTimeout(this.timerSucesso);
    this.liberarPorBaixo?.();
    this.liberarPorBaixo = null;
    if (this._porBaixo) untracked(() => this.modais.coberto.set(false));
  }

  /**
   * Modal do dashboard aberto (conta, histórico, aviso...): o painel desce para baixo dele sem
   * fechar, e a grade volta atualizada quando ele some.
   */
  @Input() set porBaixo(v: boolean) {
    if (v === this._porBaixo) return;
    this._porBaixo = v;
    // O setter roda dentro do template do dashboard; untracked deixa escrever no signal dos modais.
    untracked(() => this.modais.coberto.set(v));
    if (v) {
      // A grade fica coberta: não adianta o refresh de 30 s.
      this.liberarPorBaixo = untracked(() => this.modais.abrir());
      return;
    }
    const liberar = this.liberarPorBaixo;
    this.liberarPorBaixo = null;
    if (liberar) untracked(liberar);
    void this.grade?.recarregar(true);
  }
  get porBaixo(): boolean { return this._porBaixo; }
  private _porBaixo = false;
  private liberarPorBaixo: (() => void) | null = null;

  @HostBinding('class.por-baixo') get classePorBaixo(): boolean { return this._porBaixo; }

  @HostListener('document:keydown.escape')
  aoTeclarEsc() {
    if (this.modais.coberto()) return;
    this.fecharMenu();
  }

  /**
   * Senha chamada no guichê com a Agenda aberta (rodízio, contagem automática): o painel cobre o
   * dashboard, então o aviso aparece aqui. Só avisa quando a senha muda com o painel já aberto.
   */
  @Input() set senhaChamada(v: string | null) {
    const nova = v || null;
    const anterior = this._senhaChamada;
    this._senhaChamada = nova;
    if (!this.senhaRecebida) { this.senhaRecebida = true; return; }
    if (!nova) this.avisoSenha = null;
    else if (nova !== anterior) this.avisoSenha = nova;
  }
  get senhaChamada(): string | null { return this._senhaChamada; }
  private _senhaChamada: string | null = null;
  private senhaRecebida = false;
  avisoSenha: string | null = null;

  /** Estado da chamada automática, que fica na barra do dashboard; null = guichê sem contagem. */
  @Input() chamadaAutomatica: { texto: string; podePausar: boolean } | null = null;
  @Output() pausarChamada = new EventEmitter<void>();

  irParaAtendimento() {
    this.avisoSenha = null;
    this.fechar.emit();
  }

  // A unidade só muda por um novo login, que pode acontecer em outra aba.
  @HostListener('window:storage', ['$event'])
  async aoMudarStorage(e: StorageEvent) {
    if (e.key !== 'currentUnit' || e.newValue === e.oldValue) return;
    this.cancelarAuto();
    this.fecharMenu();
    this.fecharModais();
    this.modalDisponibilidade = false;
    this.filtroAplicado.set(null);
    this.f = filtroInicial(this.f.data);
    if (this.visao !== 'agendas') this.visao = 'dia';
    this.diasMes = [];
    await this.iniciar(null);
  }

  get ehIntegrado(): boolean { return this.f.tipo === 'INTEGRADO'; }
  get mostraEspecialidade(): boolean { return tipoComEspecialidade(this.f.tipo); }
  get pronto(): boolean { return filtroPronto(this.f); }
  get dica(): string { return dicaFiltro(this.f); }

  /**
   * Classificações do tipo escolhido. O filtro manda o rótulo: o back compara com a descrição,
   * e no exame o "valor" é o código.
   */
  get opcoesClassif(): AgendaClassificacao[] { return opcoesClassificacao(this.classificacoes, this.f.tipo); }

  get rotuloData(): string {
    const [a, m, d] = this.f.data.split('-').map(Number);
    const dia = new Date(a, m - 1, d);
    return `${DIAS_SEMANA[dia.getDay()]}, ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${a}`;
  }

  get chips(): AgendaChip[] {
    const c: AgendaChip[] = [];
    const tipo = this.tipos.find(t => t.valor === this.f.tipo);
    if (tipo) c.push({ chave: 'tipo', rotulo: tipo.rotulo, removivel: false });
    const est = this.estabelecimentos.find(e => e.cdEstabelecimento === this.f.cdEstabelecimento);
    if (est) c.push({ chave: 'estabelecimento', rotulo: est.nmEstabelecimento || est.sigla || String(est.cdEstabelecimento), removivel: false });
    if (this.mostraEspecialidade && this.f.cdEspecialidade) {
      const esp = this.especialidades.find(e => e.codigo === this.f.cdEspecialidade);
      c.push({ chave: 'especialidade', rotulo: esp?.descricao ?? String(this.f.cdEspecialidade), removivel: true });
    }
    if (this.ehIntegrado && this.f.cdMedico) {
      const med = this.medicos.find(m => m.cdMedico === this.f.cdMedico);
      c.push({ chave: 'medico', rotulo: med?.nmMedico ?? String(this.f.cdMedico), removivel: true });
    }
    if (!this.ehIntegrado && this.f.cdAgenda) {
      const ag = this.agendas.find(a => a.cdAgenda === this.f.cdAgenda);
      c.push({ chave: 'agenda', rotulo: ag?.dsAgenda ?? String(this.f.cdAgenda), removivel: true });
    }
    if (this.f.ieClassifAgenda) {
      c.push({ chave: 'classif', rotulo: this.f.ieClassifAgenda, removivel: true });
    }
    if (this.f.cancelados) c.push({ chave: 'cancelados', rotulo: 'Com cancelados', removivel: true });
    if (this.f.soAgendados) c.push({ chave: 'soAgendados', rotulo: 'Somente agendados', removivel: true });
    if (this.f.soLivres) c.push({ chave: 'soLivres', rotulo: 'Somente livres', removivel: true });
    return c;
  }

  // --- Carga inicial ------------------------------------------------------------------------

  async iniciar(entrada: AgendaFiltroEntrada | null) {
    this.erro = '';
    const atual = Number(this.localStorage.get('currentUnit'));
    this.unidade = Number.isFinite(atual) && atual > 0 ? atual : null;
    this.unidadeLogada = this.unidade;
    await Promise.all([this.carregarContexto(), this.carregarClassificacoes()]);
    if (entrada) await this.aplicarEntrada(entrada);
    else await this.carregarEstabelecimentos();
  }

  /**
   * Repete a cascata tipo→unidade→especialidade→agenda com o filtro recebido. Com agenda, aplica
   * na hora; se ela não aparecer na lista carregada (ex.: data passada com turno encerrado),
   * injeta a opção em vez de recusar.
   */
  private async aplicarEntrada(e: AgendaFiltroEntrada) {
    const data = e.data && /^\d{4}-\d{2}-\d{2}$/.test(e.data) ? e.data : this.f.data;
    // Integrado é por médico, sem agenda: vindo de uma agenda específica cai em CONSULTA.
    let tipo: AgendaTipoFiltro = e.tipo ?? 'CONSULTA';
    if (e.cdAgenda && tipo === 'INTEGRADO') tipo = 'CONSULTA';
    if (tipo === 'ODONTOLOGIA' && !this.temOdonto) tipo = 'CONSULTA';
    this.f = {
      ...filtroInicial(data), tipo,
      cdEstabelecimento: e.cdEstabelecimento ?? null,
      cdEspecialidade: tipoComEspecialidade(tipo) ? e.cdEspecialidade ?? null : null,
    };
    await this.carregarEstabelecimentos();
    // Unidade fora da lista do tipo: a cascata já caiu na unidade atual e a agenda não vale lá.
    if (!e.cdAgenda || !e.cdEstabelecimento || this.f.cdEstabelecimento !== e.cdEstabelecimento) return;
    const cdAgenda = e.cdAgenda;
    if (!this.agendas.some(a => a.cdAgenda === cdAgenda)) {
      this.agendas = [...this.agendas, {
        cdAgenda, dsAgenda: e.nmAgenda || `Agenda ${cdAgenda}`, dsObservacao: null,
        cdPessoaFisica: null, cdEstabelecimento: e.cdEstabelecimento, ieTipoAtendimento: null,
      }];
    }
    this.f = { ...this.f, cdAgenda };
    this.aplicar(false);
  }

  private async carregarContexto() {
    try {
      const ctx = await this.agendaService.contexto();
      this.contexto.set(ctx);
      if (ctx?.cdEstabelecimento) this.unidade = ctx.cdEstabelecimento;
      if (!ctx) this.erro = 'Não foi possível identificar a unidade da agenda.';
    } catch (e) {
      this.contexto.set(null);
      this.erro = mensagemErroAgenda(e, 'Não foi possível carregar o contexto da agenda.');
    }
  }

  private async carregarClassificacoes() {
    if (this.classificacoes.length) return;
    try {
      this.classificacoes = await this.agendaService.classificacoes();
    } catch {
      this.classificacoes = [];
    }
  }

  // --- Cascata ------------------------------------------------------------------------------

  async escolherTipo(tipo: AgendaTipoFiltro) {
    if (this.f.tipo === tipo) return;
    this.f = { ...this.f, tipo, cdEspecialidade: null, cdAgenda: null, cdMedico: null, ieClassifAgenda: null };
    this.especialidades = [];
    this.agendas = [];
    this.medicos = [];
    this.aposMudanca();
    await this.carregarEstabelecimentos();
  }

  async trocarEstabelecimento(cd: number | null) {
    this.f = { ...this.f, cdEstabelecimento: cd, cdEspecialidade: null, cdAgenda: null, cdMedico: null };
    this.aposMudanca();
    await this.carregarDependentes();
  }

  async trocarEspecialidade(cd: number | null) {
    this.f = { ...this.f, cdEspecialidade: cd, cdAgenda: null };
    this.aposMudanca();
    await this.carregarAgendas();
  }

  trocarAgenda(cd: number | null) {
    this.f = { ...this.f, cdAgenda: cd };
    this.aposMudanca();
  }

  trocarMedico(cd: number | null) {
    this.f = { ...this.f, cdMedico: cd };
    this.aposMudanca();
  }

  trocarClassificacao(valor: string | null) {
    this.f = { ...this.f, ieClassifAgenda: valor || null };
    this.aposMudanca();
  }

  marcarCancelados(v: boolean) {
    this.f = { ...this.f, cancelados: v };
    this.aposMudanca();
  }

  // Somente agendados e somente livres se excluem: juntos a grade viria sempre vazia.
  marcarSoAgendados(v: boolean) {
    this.f = { ...this.f, soAgendados: v, soLivres: v ? false : this.f.soLivres };
    this.aposMudanca();
  }

  marcarSoLivres(v: boolean) {
    this.f = { ...this.f, soLivres: v, soAgendados: v ? false : this.f.soAgendados };
    this.aposMudanca();
  }

  trocarData(data: string) {
    if (!data || data === this.f.data) return;
    this.f = { ...this.f, data };
    this.aposMudanca();
  }

  diaAnterior() { this.trocarData(somarDias(this.f.data, -1)); }
  diaSeguinte() { this.trocarData(somarDias(this.f.data, 1)); }
  hoje() { this.trocarData(dataLocal(new Date())); }

  filtrar() {
    if (!this.pronto) return;
    this.aplicar(false);
  }

  async limpar() {
    this.f = { ...filtroInicial(this.f.data), cdEstabelecimento: this.f.cdEstabelecimento };
    this.aposMudanca();
    await this.carregarEstabelecimentos();
  }

  /** Tirar um chip é ação explícita: busca na hora se o filtro continua completo. */
  async removerChip(chave: AgendaChip['chave']) {
    switch (chave) {
      case 'especialidade':
        this.f = { ...this.f, cdEspecialidade: null };
        await this.carregarAgendas(true);
        break;
      case 'agenda': this.f = { ...this.f, cdAgenda: null }; break;
      case 'medico': this.f = { ...this.f, cdMedico: null }; break;
      case 'classif': this.f = { ...this.f, ieClassifAgenda: null }; break;
      case 'cancelados': this.f = { ...this.f, cancelados: false }; break;
      case 'soAgendados': this.f = { ...this.f, soAgendados: false }; break;
      case 'soLivres': this.f = { ...this.f, soLivres: false }; break;
      default: return;
    }
    this.cancelarAuto();
    if (this.pronto) this.aplicar(false);
    else this.filtroAplicado.set(null);
  }

  /**
   * Depois de cada mudança: filtro incompleto limpa a grade; filtro que aponta para algo
   * específico (ou só mudou data/classificação/caixas de um filtro já aplicado) busca sozinho
   * depois da espera. Trocar especialidade sem agenda espera o Filtrar.
   */
  aposMudanca() {
    this.cancelarAuto();
    const aplicado = this.filtroAplicado();
    if (!this.pronto) {
      if (aplicado) this.filtroAplicado.set(null);
      return;
    }
    const recarrega = autoCargaPronta(this.f) || (aplicado !== null && mesmaSelecao(aplicado, this.f));
    if (!recarrega) {
      if (aplicado) this.filtroAplicado.set(null);
      return;
    }
    this.timerAuto = setTimeout(() => {
      this.timerAuto = null;
      if (filtroPronto(this.f)) this.aplicar(true);
    }, ESPERA_AUTO_CARGA_MS);
  }

  private aplicar(automatico: boolean) {
    this.cancelarAuto();
    const agenda = this.ehIntegrado ? null : this.agendas.find(a => a.cdAgenda === this.f.cdAgenda) ?? null;
    this.filtroAplicado.set({
      ...this.f,
      tipo: this.f.tipo!,
      cdEstabelecimento: this.f.cdEstabelecimento!,
      cdEspecialidade: this.ehIntegrado ? null : this.f.cdEspecialidade,
      cdAgenda: this.ehIntegrado ? null : this.f.cdAgenda,
      cdMedico: this.ehIntegrado ? this.f.cdMedico : null,
      agenda,
      automatico,
    });
    if (this.visao === 'mes') void this.carregarMes();
  }

  private cancelarAuto() {
    if (this.timerAuto) clearTimeout(this.timerAuto);
    this.timerAuto = null;
  }

  // --- Listas -------------------------------------------------------------------------------

  private async carregarEstabelecimentos() {
    const seq = ++this.seq.estabelecimentos;
    this.carregando.estabelecimentos = true;
    try {
      const integrado = this.ehIntegrado;
      const lista = await this.agendaService.estabelecimentos({
        tipo: codigoTipoAgenda(this.f.tipo),
        areaAtuacao: areaAtuacaoDoTipo(this.f.tipo),
        // No Integrado o tipo vai 0 (qualquer) e o recorte fica com quem tem médico nos dois tipos.
        doisTipos: integrado ? 1 : null,
      });
      if (seq !== this.seq.estabelecimentos) return;
      this.estabelecimentos = lista;
      const ids = new Set(lista.map(e => e.cdEstabelecimento));
      const escolhido = this.f.cdEstabelecimento && ids.has(this.f.cdEstabelecimento)
        ? this.f.cdEstabelecimento
        : this.unidade && ids.has(this.unidade) ? this.unidade : null;
      if (escolhido !== this.f.cdEstabelecimento) {
        this.f = { ...this.f, cdEstabelecimento: escolhido, cdEspecialidade: null, cdAgenda: null, cdMedico: null };
      }
    } catch (e) {
      if (seq !== this.seq.estabelecimentos) return;
      this.estabelecimentos = [];
      this.erro = mensagemErroAgenda(e, 'Erro ao carregar as unidades.');
    } finally {
      if (seq === this.seq.estabelecimentos) this.carregando.estabelecimentos = false;
    }
    if (seq !== this.seq.estabelecimentos) return;
    this.aposMudanca();
    await this.carregarDependentes();
  }

  private async carregarDependentes() {
    if (this.ehIntegrado) {
      this.especialidades = [];
      this.agendas = [];
      await this.carregarMedicos();
      return;
    }
    this.medicos = [];
    if (this.mostraEspecialidade) await this.carregarEspecialidades();
    else this.especialidades = [];
    await this.carregarAgendas();
  }

  private async carregarEspecialidades() {
    const seq = ++this.seq.especialidades;
    if (!this.f.cdEstabelecimento) { this.especialidades = []; return; }
    this.carregando.especialidades = true;
    try {
      const lista = await this.agendaService.especialidades({
        cdEstabelecimento: this.f.cdEstabelecimento,
        tipo: codigoTipoAgenda(this.f.tipo),
        areaAtuacao: areaAtuacaoDoTipo(this.f.tipo),
      });
      if (seq === this.seq.especialidades) this.especialidades = lista;
    } catch (e) {
      if (seq !== this.seq.especialidades) return;
      this.especialidades = [];
      this.erro = mensagemErroAgenda(e, 'Erro ao carregar as especialidades.');
    } finally {
      if (seq === this.seq.especialidades) this.carregando.especialidades = false;
    }
  }

  /** preservar: mantém a agenda escolhida se ela ainda estiver na lista nova. */
  private async carregarAgendas(preservar = false) {
    const seq = ++this.seq.agendas;
    if (!this.f.cdEstabelecimento || this.ehIntegrado) { this.agendas = []; return; }
    this.carregando.agendas = true;
    try {
      const lista = await this.agendaService.agendas({
        cdEstabelecimento: this.f.cdEstabelecimento,
        cdEspecialidade: this.mostraEspecialidade ? this.f.cdEspecialidade : null,
        tipo: codigoTipoAgenda(this.f.tipo),
        areaAtuacao: areaAtuacaoDoTipo(this.f.tipo),
      });
      if (seq !== this.seq.agendas) return;
      this.agendas = lista;
      if (this.f.cdAgenda && !(preservar && lista.some(a => a.cdAgenda === this.f.cdAgenda))) {
        this.f = { ...this.f, cdAgenda: null };
      }
    } catch (e) {
      if (seq !== this.seq.agendas) return;
      this.agendas = [];
      this.erro = mensagemErroAgenda(e, 'Erro ao carregar as agendas.');
    } finally {
      if (seq === this.seq.agendas) this.carregando.agendas = false;
    }
  }

  private async carregarMedicos() {
    const seq = ++this.seq.medicos;
    if (!this.f.cdEstabelecimento) { this.medicos = []; return; }
    this.carregando.medicos = true;
    try {
      // Sem especialidade de propósito: consulta e exame do mesmo médico têm especialidades diferentes.
      const lista = await this.agendaService.medicos({ cdEstabelecimento: this.f.cdEstabelecimento, doisTipos: 1 });
      if (seq === this.seq.medicos) this.medicos = lista;
    } catch (e) {
      if (seq !== this.seq.medicos) return;
      this.medicos = [];
      this.erro = mensagemErroAgenda(e, 'Erro ao carregar os médicos.');
    } finally {
      if (seq === this.seq.medicos) this.carregando.medicos = false;
    }
  }

  // --- Visão mensal ---------------------------------------------------------------------------

  get celulas() {
    return celulasMes(this.mesAtual, this.diasMes, dataLocal(new Date()));
  }

  get rotuloMesAtual(): string {
    return rotuloMes(this.mesAtual);
  }

  /** Igual à grade: exige agenda escolhida, ou médico no Integrado. */
  get mesPronto(): boolean {
    return autoCargaPronta(this.f);
  }

  mudarVisao(v: AgendaVisao) {
    if (this.visao === v) return;
    this.fecharMenu();
    this.visao = v;
    if (v === 'mes') {
      this.mesAtual = this.f.data.slice(0, 7);
      void this.carregarMes();
    }
  }

  mesAnterior() {
    this.mesAtual = mesAnteriorDe(this.mesAtual);
    void this.carregarMes();
  }

  mesSeguinte() {
    this.mesAtual = mesSeguinteDe(this.mesAtual);
    void this.carregarMes();
  }

  /** Clique no dia: volta para a visão do dia já naquela data. */
  abrirDiaDoMes(data: string) {
    this.visao = 'dia';
    this.trocarData(data);
  }

  // --- Agendas do Dia --------------------------------------------------------------------------

  /** Unidade escolhida nos filtros; enquanto a cascata carrega, a do contexto. */
  get unidadeAgendasDoDia(): number | null {
    return this.f.cdEstabelecimento ?? this.contexto()?.cdEstabelecimento ?? this.unidade;
  }

  get nomeUnidadeAgendasDoDia(): string {
    const cd = this.unidadeAgendasDoDia;
    const e = this.estabelecimentos.find(x => x.cdEstabelecimento === cd);
    return e ? e.nmEstabelecimento || e.sigla || String(e.cdEstabelecimento) : '';
  }

  /** Linha das Agendas do Dia: grade daquela agenda e data, pela mesma cascata do filtro inicial. */
  async abrirDaListaDoDia(e: AgendaFiltroEntrada) {
    this.fecharMenu();
    this.visao = 'dia';
    await this.aplicarEntrada(e);
  }

  async carregarMes() {
    const seq = ++this.seqMes;
    if (!this.mesPronto) { this.diasMes = []; this.erroMes = ''; return; }
    this.carregandoMes = true;
    this.erroMes = '';
    try {
      const dias = await this.agendaService.mes({
        mes: this.mesAtual,
        cdEstabelecimento: this.f.cdEstabelecimento,
        cdEspecialidade: this.ehIntegrado ? null : this.f.cdEspecialidade,
        cdAgenda: this.ehIntegrado ? null : this.f.cdAgenda,
        cdMedicoAgenda: this.ehIntegrado ? this.f.cdMedico : null,
        tipo: this.f.tipo,
      });
      if (seq !== this.seqMes) return;
      this.diasMes = dias ?? [];
    } catch (e) {
      if (seq !== this.seqMes) return;
      this.diasMes = [];
      this.erroMes = mensagemErroAgenda(e, 'Não foi possível carregar o mês.');
    } finally {
      if (seq === this.seqMes) this.carregandoMes = false;
    }
  }

  // --- Menu ⋮/botão direito da linha (+ atalhos para o que a Fila já tem) ----------------------

  /**
   * Multi-seleção mantida (clique direito numa linha já marcada): mesmo menu da barra, com
   * contagem no rótulo e sem os atalhos "Já existe na Fila" (não há um paciente só). Senão, o
   * menu de uma linha, igual ao ⋮ de sempre.
   */
  get itensMenu(): AgendaMenuLinha {
    const m = this.menu;
    if (!m) return { principais: [], fila: [] };
    if (m.alvo.length > 1) return { principais: itensMenuSelecao(m.alvo), fila: [] };
    return itensMenuLinha(m.linha, !!this.contexto()?.supervisor, { pode: m.podeEncaixe, motivo: m.motivoEncaixe }, this.regraFilaMedicaDe(m.linha.cdAgenda));
  }

  /** No Integrado a grade é por médico; a agenda de cada linha fica no menu dela. */
  get filaMedicaBarra(): AgendaMenuRegra {
    const f = this.filtroAplicado();
    if (f?.tipo === 'INTEGRADO') return { pode: false, motivo: 'No Integrado, abra a Fila médica pelo menu da linha' };
    return this.regraFilaMedicaDe(f?.cdAgenda);
  }

  private regraFilaMedicaDe(cdAgenda: number | null | undefined): AgendaMenuRegra {
    const f = this.filtroAplicado();
    return regraFilaMedica(f ? cdAgenda : null, f?.data ?? this.f.data, f?.cdEstabelecimento ?? null, this.unidadeLogada, dataLocal(new Date()));
  }

  abrirFilaMedicaDaBarra() {
    const f = this.filtroAplicado();
    if (!f?.cdAgenda || !this.filaMedicaBarra.pode) return;
    this.abrirFilaMedica.emit({ cdAgenda: f.cdAgenda, nmAgenda: f.agenda?.dsAgenda ?? null });
  }

  abrirMenuLinha(ev: AgendaMenuLinhaEvento) {
    this.fecharMenu();
    let top: number; let left: number;
    if (ev.ponto) {
      const win = typeof window !== 'undefined' ? window : null;
      ({ top, left } = posicaoMenu(ev.ponto.x, ev.ponto.y, win?.innerWidth ?? 1280, win?.innerHeight ?? 800));
    } else if (ev.alvo) {
      const r = ev.alvo.getBoundingClientRect();
      top = r.bottom + 4;
      left = Math.max(8, r.right - 260);
    } else {
      return;
    }
    const marcadas = ev.marcadas ?? [];
    const alvo = marcadas.length > 1 && ehMarcado(ev.linha) ? marcadas : [ev.linha];
    this.menu = { linha: ev.linha, alvo, podeEncaixe: !!ev.podeEncaixe, motivoEncaixe: ev.motivoEncaixe ?? '', top, left };
    this.fecharMenuModal = this.modais.abrir();
  }

  fecharMenu() {
    if (this.fecharMenuModal) { this.fecharMenuModal(); this.fecharMenuModal = null; }
    this.menu = null;
  }

  @HostListener('window:scroll')
  aoRolarJanela() {
    this.fecharMenu();
  }

  clicarItemMenu(item: AgendaMenuItem) {
    if (item.desabilitado || !this.menu) return;
    const { linha, alvo } = this.menu;
    this.fecharMenu();
    switch (item.acao) {
      case 'novo': this.aoNovoAgendamento(linha); break;
      case 'encaixe': this.aoEncaixe(); break;
      case 'reservar': this.aoAcaoGrade({ acao: 'reservar', linhas: [linha] }); break;
      case 'liberar': this.aoAcaoGrade({ acao: 'liberarReserva', linhas: [linha] }); break;
      case 'alterar': this.aoAcaoGrade({ acao: 'alterar', linhas: alvo }); break;
      case 'transferir': this.aoAcaoGrade({ acao: 'transferir', linhas: alvo }); break;
      case 'cancelar': this.aoAcaoGrade({ acao: 'cancelar', linhas: alvo }); break;
      case 'trocarPaciente': this.aoAcaoGrade({ acao: 'trocarPaciente', linhas: alvo }); break;
      case 'desvincular': this.aoAcaoGrade({ acao: 'desvincular', linhas: alvo }); break;
      case 'filaMedica': this.abrirFilaMedica.emit({ cdAgenda: linha.cdAgenda, nmAgenda: linha.dsAgenda ?? null }); break;
      case 'abrirAtendimento': break;
      case 'conta': this.irParaPaciente(linha, true); break;
      case 'historico': this.irParaPaciente(linha, false); break;
    }
  }

  // --- Modais de agendamento, encaixe e reserva ----------------------------------------------

  aoNovoAgendamento(linha: AgendaGradeLinha): void {
    if (!this.filtroAplicado()) return;
    this.modalAgendamento = { modo: 'novo', linha };
  }

  aoEncaixe(): void {
    const f = this.filtroAplicado();
    if (!f) return;
    if (!podeEncaixar(this.contexto())) { this.erro = 'Encaixe é somente para supervisão.'; return; }
    if (!f.cdAgenda || f.tipo === 'INTEGRADO') { this.erro = 'Escolha uma agenda para encaixar.'; return; }
    this.modalAgendamento = { modo: 'encaixe', linha: null };
  }

  aoAcaoGrade(ev: AgendaAcaoGradeEvento): void {
    switch (ev.acao) {
      case 'reservar':
      case 'liberarReserva':
        if (ev.linhas.length !== 1) return;
        if (!this.contexto()?.supervisor) { this.erro = 'Reservar e liberar horário é somente para supervisão.'; return; }
        this.modalReserva = ev.linhas[0];
        break;
      case 'transferir':
        if (ev.linhas.length) this.modalTransferir = ev.linhas;
        break;
      case 'alterar':
      case 'cancelar':
      case 'trocarPaciente':
      case 'desvincular': {
        const motivo = motivoAcaoSelecao(ev.acao, ev.linhas);
        if (motivo) { this.erro = motivo; return; }
        const marcadas = ev.linhas.filter(ehMarcado);
        if (ev.acao === 'alterar') this.modalAlterar = marcadas;
        else if (ev.acao === 'cancelar') this.modalCancelar = marcadas;
        else if (ev.acao === 'trocarPaciente') this.modalTrocarPaciente = marcadas[0];
        else this.modalDesvincular = marcadas[0];
        break;
      }
    }
  }

  fecharModais() {
    this.modalAgendamento = null;
    this.modalReserva = null;
    this.modalTransferir = null;
    this.modalAlterar = null;
    this.modalCancelar = null;
    this.modalTrocarPaciente = null;
    this.modalDesvincular = null;
  }

  // --- Encontrar disponibilidade -------------------------------------------------------------

  abrirDisponibilidade() {
    this.fecharMenu();
    this.modalDisponibilidade = true;
  }

  fecharDisponibilidade() {
    this.modalDisponibilidade = false;
  }

  /** O novo agendamento abre por cima da busca, que continua aberta. */
  aoAgendarHorario(h: AgendaHorarioEscolhido) {
    this.modalAgendamento = { modo: 'novo', linha: h.linha, filtro: h.filtro, exame: h.exame, paciente: h.paciente ?? null };
  }

  /** Alteração e cancelamento em lote ficam abertos com o resultado por linha; só a grade recarrega. */
  async aoConcluirLote() {
    await this.grade?.recarregar();
  }

  /** Depois de gravar: fecha, avisa e recarrega a grade sem piscar. */
  async aoSalvarModal(mensagem: string) {
    this.fecharModais();
    this.sucesso = mensagem;
    if (this.timerSucesso) clearTimeout(this.timerSucesso);
    this.timerSucesso = setTimeout(() => { this.sucesso = ''; this.timerSucesso = null; }, DURACAO_SUCESSO_MS);
    // Com a busca aberta o aviso da página fica coberto: ela mostra o dela e refaz as vagas.
    await Promise.all([this.grade?.recarregar(), this.modalDisponibilidade ? this.disponibilidade?.aposAgendar(mensagem) : null]);
  }

  private irParaPaciente(linha: AgendaGradeLinha, conta: boolean) {
    const p = linha.paciente;
    if (!p?.cdPessoaFisica) return;
    const paciente: AgendaPacienteFila = {
      cdPessoaFisica: Number(p.cdPessoaFisica),
      nome: p.nmPaciente ?? '',
      cpf: p.nrCpf ?? '',
      nrAtendimento: p.nrAtendimento ?? null,
    };
    if (conta && paciente.nrAtendimento) this.abrirConta.emit(paciente);
    else this.abrirHistorico.emit(paciente);
  }
}

